/**
 * Client de l'API.
 *
 * ── Le jeton d'accès ne quitte jamais la mémoire ────────────────────────────
 * Ni `localStorage`, ni `sessionStorage`, ni cookie lisible : une XSS y
 * accéderait. Il vit dans une variable de module, perdue au rechargement de la
 * page — et récupérée par un rafraîchissement silencieux, puisque le cookie de
 * rafraîchissement, lui, est `HttpOnly` et hors de portée du JavaScript.
 *
 * ── Le rafraîchissement est en FILE UNIQUE ──────────────────────────────────
 * Un tableau de bord déclenche plusieurs requêtes en parallèle. Si le jeton
 * vient d'expirer, elles reçoivent toutes 401 en même temps. Sans coordination,
 * chacune lance son propre rafraîchissement : la rotation avec détection de
 * rejeu (phase 1) voit alors un jeton déjà consommé réapparaître, conclut au
 * vol, et **déconnecte l'utilisateur de toutes ses sessions**.
 *
 * D'où `rafraichissementEnCours` : la première requête rafraîchit, les autres
 * attendent le même résultat.
 */

export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  code: string;
  detail: string;
  traceId: string;
  errors?: unknown;
}

/** Erreur portant la réponse normalisée de l'API. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly traceId: string;
  readonly errors: unknown;

  constructor(probleme: ProblemDetails) {
    super(probleme.detail);
    this.name = 'ApiError';
    this.status = probleme.status;
    this.code = probleme.code;
    this.traceId = probleme.traceId;
    this.errors = probleme.errors;
  }

  /** Vrai si l'utilisateur peut agir : quota, offre, validation. */
  get actionnable(): boolean {
    return ['QUOTA_EXCEEDED', 'SEATS_EXCEEDED', 'VALIDATION_ERROR', 'CONFLICT', 'TENANT_SUSPENDED'].includes(
      this.code,
    );
  }
}

export interface OptionsRequete {
  methode?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  corps?: unknown;
  /** Ne tente pas de rafraîchir sur 401 : réservé aux routes d'authentification. */
  sansRafraichissement?: boolean;
  signal?: AbortSignal;
  /**
   * Laisse la requête PARTIR même si la page se ferme.
   *
   * Pour le dernier enregistrement d'une saisie automatique : sans cela, la
   * requête lancée au moment où l'onglet se referme est annulée, et la frappe
   * des dernières secondes est perdue. `sendBeacon` ferait le même travail mais
   * n'accepte aucun en-tête — donc ni le jeton, ni la classe de travail.
   */
  garderEnVie?: boolean;
  /**
   * Un FICHIER envoyé tel quel, à la place d'un corps JSON.
   *
   * Encodé en base64 dans du JSON, il pèserait un tiers de plus — un document
   * de 10 Mo en ferait 13,4. Son nom part dans l'en-tête `x-file-name`, encodé :
   * dans l'adresse, il finirait dans les journaux du proxy.
   */
  fichier?: Blob & { name: string };
  /**
   * La classe de CETTE requête, à la place de la classe de travail.
   *
   * Pour le module Impressions, qui imprime une classe d'une autre année sans
   * changer la classe de travail de l'application. Le serveur vérifie, comme
   * pour l'en-tête ordinaire, qu'elle est à la portée de l'utilisateur.
   */
  classe?: string;
}

/** Un fichier produit par le serveur à partir d'un envoi : le PDF d'un dossier. */
export interface OptionsTelechargement {
  methode?: 'GET' | 'POST';
  corps?: unknown;
  /** La classe de cette requête, comme pour `requete`. */
  classe?: string;
}

export interface ClientApi {
  requete<T>(chemin: string, options?: OptionsRequete): Promise<T>;
  /** Pose le jeton après une connexion réussie. */
  definirJeton(jeton: string | null): void;
  /** Classe de travail explicite. `null` = laisser le serveur décider. */
  definirClasse(classe: string | null): void;
  jetonActuel(): string | null;
  /** Tente de restaurer une session depuis le cookie. Faux si aucune. */
  restaurerSession(): Promise<boolean>;
  /** Prévient d'une déconnexion : session expirée ou révoquée. */
  surDeconnexion(rappel: () => void): () => void;
  /**
   * Ouvre une consultation SUPPORT avec le jeton remis par le back-office.
   *
   * Le rafraîchissement par cookie est alors coupé : ce cookie, s'il existe,
   * est celui du compte PERSONNEL de l'opérateur dans ce navigateur. Le
   * reprendre à l'expiration de la consultation le ferait basculer, sans un
   * mot, dans un autre espace que celui qu'il examinait.
   */
  ouvrirImitation(jeton: string): void;
  /**
   * Télécharge un fichier servi par l'API.
   *
   * ── Pourquoi cela ne peut pas être un simple lien ─────────────────────────
   * L'export RGPD était déclenché par `location.href = '/api/v1/rgpd/export'`.
   * Une navigation du navigateur n'envoie AUCUN en-tête `Authorization`, et le
   * jeton d'accès ne vit qu'en mémoire — jamais en cookie, précisément pour
   * qu'une XSS ne puisse pas le lire. Le serveur répondait donc 401, et le
   * bouton ne produisait rien.
   *
   * Le fichier est donc récupéré comme n'importe quelle requête — jeton
   * compris, rafraîchissement compris — puis remis au navigateur en mémoire.
   */
  telecharger(chemin: string, nomParDefaut: string, options?: OptionsTelechargement): Promise<void>;
}

export function creerClientApi(baseUrl = ''): ClientApi {
  let jeton: string | null = null;
  let classe: string | null = null;
  let rafraichissementEnCours: Promise<boolean> | null = null;
  let imitation = false;
  const abonnes = new Set<() => void>();

  const prevenirDeconnexion = () => {
    jeton = null;
    for (const rappel of abonnes) rappel();
  };

  /**
   * Rafraîchit le jeton. Un seul appel réseau, quel que soit le nombre
   * d'appelants simultanés.
   */
  async function rafraichir(): Promise<boolean> {
    if (imitation) return false;
    if (rafraichissementEnCours) return rafraichissementEnCours;

    rafraichissementEnCours = (async () => {
      try {
        const reponse = await fetch(`${baseUrl}/api/v1/auth/refresh`, {
          method: 'POST',
          // Indispensable : sans cela le cookie HttpOnly n'est pas transmis.
          credentials: 'include',
        });
        if (!reponse.ok) return false;
        const donnees = (await reponse.json()) as { accessToken: string };
        jeton = donnees.accessToken;
        return true;
      } catch {
        return false;
      } finally {
        // Libéré dans tous les cas : sinon un échec réseau condamnerait
        // définitivement le rafraîchissement pour la durée de la page.
        rafraichissementEnCours = null;
      }
    })();

    return rafraichissementEnCours;
  }

  async function envoyer<T>(chemin: string, options: OptionsRequete, reessai: boolean): Promise<T> {
    const entetes: Record<string, string> = {};
    if (options.corps !== undefined) entetes['content-type'] = 'application/json';
    if (options.fichier) {
      entetes['content-type'] = 'application/octet-stream';
      entetes['x-file-name'] = encodeURIComponent(options.fichier.name);
    }
    if (jeton) entetes['authorization'] = `Bearer ${jeton}`;
    /*
     * Classe de travail, quand elle a été choisie explicitement.
     *
     * Le serveur sait déjà déduire la classe d'un enseignant qui n'en a qu'une
     * — c'est le cas courant, et il ne doit jamais avoir à choisir. L'en-tête
     * ne sert qu'à celui qui en suit plusieurs : un directeur, ou un
     * remplaçant. Une classe hors de portée est REFUSÉE par le serveur, pas
     * ignorée.
     */
    const classeRequete = options.classe ?? classe;
    if (classeRequete) entetes['x-classroom-id'] = classeRequete;

    /*
     * ── Une panne de réseau n'est pas un message en anglais ────────────────
     *
     * `fetch` REJETTE quand le serveur est injoignable — connexion coupée,
     * application redémarrée, réseau de l'école qui tombe. Sans ce
     * rattrapage, l'écran affichait le message du navigateur, « Failed to
     * fetch » : incompréhensible, et qui laisse croire à un bug du logiciel
     * plutôt qu'à une coupure passagère.
     *
     * L'annulation volontaire (`AbortController`, quand on change d'écran
     * avant la fin) reste distincte : elle continue de remonter telle quelle,
     * sans quoi chaque navigation afficherait une erreur.
     */
    let reponse: Response;
    try {
      reponse = await fetch(`${baseUrl}/api/v1${chemin}`, {
        method: options.methode ?? 'GET',
        headers: entetes,
        credentials: 'include',
        ...(options.corps !== undefined ? { body: JSON.stringify(options.corps) } : {}),
        ...(options.fichier ? { body: options.fichier } : {}),
        ...(options.signal ? { signal: options.signal } : {}),
        ...(options.garderEnVie ? { keepalive: true } : {}),
      });
    } catch (echec) {
      if (options.signal?.aborted || (echec as Error)?.name === 'AbortError') throw echec;
      throw new ApiError({
        type: 'about:blank',
        title: 'Connexion impossible',
        status: 0,
        code: 'RESEAU',
        detail: 'Le serveur n’a pas répondu. Vérifiez votre connexion, puis réessayez : '
          + 'rien de ce que vous avez saisi n’est perdu.',
        traceId: '',
      });
    }

    // 401 : le jeton a expiré. On rafraîchit UNE fois, puis on rejoue.
    if (reponse.status === 401 && reessai && !options.sansRafraichissement) {
      if (await rafraichir()) return envoyer<T>(chemin, options, false);
      prevenirDeconnexion();
    }

    if (reponse.status === 204) return undefined as T;

    const texte = await reponse.text();
    /*
     * Une réponse qui n'est pas du JSON vient du PROXY, pas de l'API : la page
     * d'erreur de Nginx pour un envoi trop lourd, par exemple. L'analyser
     * levait une erreur de syntaxe, affichée telle quelle.
     */
    let donnees: unknown;
    try {
      donnees = texte ? JSON.parse(texte) : undefined;
    } catch {
      donnees = reponse.status === 413
        ? { detail: 'Envoi refusé : le fichier est trop lourd.' }
        : reponse.status === 429 || reponse.status === 503
          ? { detail: 'Le serveur reçoit trop de demandes à la fois. Patientez quelques secondes, puis réessayez.' }
          : undefined;
    }

    if (!reponse.ok) {
      const probleme = donnees as Partial<ProblemDetails> | undefined;
      throw new ApiError({
        type: probleme?.type ?? 'about:blank',
        title: probleme?.title ?? 'Erreur',
        status: reponse.status,
        code: probleme?.code ?? 'INTERNAL',
        // Un message vide laisserait l'utilisateur devant un écran muet.
        detail: probleme?.detail ?? 'Une erreur est survenue.',
        traceId: probleme?.traceId ?? '',
        ...(probleme?.errors !== undefined ? { errors: probleme.errors } : {}),
      });
    }

    return donnees as T;
  }

  return {
    requete: (chemin, options = {}) => envoyer(chemin, options, true),

    async telecharger(chemin, nomParDefaut, options = {}) {
      const classeRequete = options.classe ?? classe;
      const appeler = () => fetch(`${baseUrl}/api/v1${chemin}`, {
        method: options.methode ?? 'GET',
        headers: {
          ...(jeton ? { authorization: `Bearer ${jeton}` } : {}),
          // La classe de travail, comme pour toute requête : une pièce jointe
          // de fiche appartient à une classe.
          ...(classeRequete ? { 'x-classroom-id': classeRequete } : {}),
          ...(options.corps !== undefined ? { 'content-type': 'application/json' } : {}),
        },
        credentials: 'include',
        ...(options.corps !== undefined ? { body: JSON.stringify(options.corps) } : {}),
      });

      let reponse: Response;
      try {
        reponse = await appeler();
      } catch {
        // Serveur injoignable : on le dit en français, comme ailleurs.
        throw new ApiError({
          type: 'about:blank',
          title: 'Connexion impossible',
          status: 0,
          code: 'RESEAU',
          detail: 'Le serveur n’a pas répondu : le fichier n’a pas pu être récupéré. '
            + 'Vérifiez votre connexion, puis réessayez.',
          traceId: '',
        });
      }

      // Même rattrapage que pour une requête ordinaire : un jeton expiré ne
      // doit pas transformer un export en échec silencieux.
      if (reponse.status === 401 && await rafraichir()) reponse = await appeler();

      if (!reponse.ok) {
        /*
         * On ne déconnecte QUE sur un 401 que le rafraîchissement n'a pas
         * sauvé. Toute autre erreur — un export trop lourd, un refus de droit,
         * une panne passagère — déconnectait aussi, et l'on perdait sa session
         * pour un fichier qui n'était pas venu.
         */
        if (reponse.status === 401) prevenirDeconnexion();
        // Le message du serveur quand il en donne un : « PDF indisponible sur
        // ce serveur » dit quoi faire, « fichier non récupéré » non.
        let detail = "Le fichier n'a pas pu être récupéré.";
        let code = reponse.status === 401 ? 'UNAUTHORIZED' : 'INTERNAL';
        try {
          const probleme = await reponse.json() as Partial<ProblemDetails>;
          if (probleme.detail && (reponse.status < 500 || reponse.status === 503)) detail = probleme.detail;
          if (probleme.code) code = probleme.code;
        } catch {
          /*
           * Pas de JSON : la réponse vient du PROXY, pas de l'API. Un 429 ou un
           * 503 y signifie « trop de demandes à la fois » : on le dit, et le
           * code permet à l'appelant de réessayer plutôt que d'abandonner.
           */
          if (reponse.status === 429 || reponse.status === 503) {
            code = 'RATE_LIMITED';
            detail = 'Le serveur reçoit trop de demandes à la fois. Patientez quelques secondes, puis réessayez.';
          }
        }
        throw new ApiError({
          type: 'about:blank',
          title: 'Téléchargement impossible',
          status: reponse.status,
          code,
          detail,
          traceId: '',
        });
      }

      /*
       * Le nom vient du serveur quand il le donne : `content-disposition`
       * porte déjà le slug de l'école et la date, ce qui distingue deux
       * exports faits à un mois d'intervalle.
       */
      const disposition = reponse.headers.get('content-disposition') ?? '';
      // Le nom en UTF-8 d'abord : l'autre n'est qu'un repli sans accents.
      const enUtf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
      const trouve = /filename="([^"]+)"/.exec(disposition);
      let nom = trouve?.[1] ?? nomParDefaut;
      if (enUtf8?.[1]) {
        try { nom = decodeURIComponent(enUtf8[1]); } catch { /* le repli suffit */ }
      }

      const blob = await reponse.blob();
      const url = URL.createObjectURL(blob);
      const lien = document.createElement('a');
      lien.href = url;
      lien.download = nom;
      document.body.appendChild(lien);
      lien.click();
      lien.remove();
      // Libéré tout de suite : un objet URL retenu garde le fichier entier en
      // mémoire, et un export d'école n'est pas petit.
      URL.revokeObjectURL(url);
    },
    definirJeton: (nouveau) => { jeton = nouveau; },
    ouvrirImitation: (jetonSupport) => { jeton = jetonSupport; imitation = true; },
    definirClasse: (nouvelle) => { classe = nouvelle; },
    jetonActuel: () => jeton,
    restaurerSession: rafraichir,
    surDeconnexion(rappel) {
      abonnes.add(rappel);
      return () => abonnes.delete(rappel);
    },
  };
}

// ── Formes échangées avec l'API ─────────────────────────────────────────────

export interface ReponseConnexion {
  accessToken?: string;
  expiresIn?: number;
  tenantId?: string;
  mfaRequired?: true;
  challengeToken?: string;
}

export interface Abonnement {
  planCode: string;
  status: string;
  interval: string | null;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  cancelAtPeriodEnd: boolean;
  writesAllowed: boolean;
  /**
   * L'espace est payé (ou en essai). Faux : il ne voit plus que la facturation
   * et l'export, et le cadre de l'application dit déjà pourquoi. Absent d'une
   * réponse plus ancienne : réputé payé.
   */
  paid?: boolean;
  features: string[];
  quotas: Array<{ metric: string; used: number; limit: number; unlimited: boolean }>;
  /**
   * Rétractation ouverte — consommateur, moins de quatorze jours après la
   * souscription payée. `remboursement` en centimes, à la date du jour.
   */
  retractation?: { jusquAu: string; joursUtilises: number; remboursement: number } | null;
}

export interface Membre {
  id: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  /** « Prénom Nom », ou l'adresse pour un compte antérieur à la saisie. */
  displayName: string;
  role: 'OWNER' | 'ADMIN' | 'MEMBER';
  acceptedAt: string | null;
  /** Au-delà des sièges payés : bientôt, ou déjà, en lecture seule. */
  siege?: 'A_ECHEANCE' | 'LECTURE_SEULE' | null;
}

export interface InvitationEnAttente {
  id: string;
  email: string;
  role: Membre['role'];
  expiresAt: string;
  createdAt: string;
}

export interface UtilisateurCourant {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  /** « Prénom Nom », ou l'adresse pour un compte antérieur à la saisie. */
  displayName: string;
  emailVerified: boolean;
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  role: Membre['role'];
  totpEnabled: boolean;
  impersonation: boolean;
}

export interface EtatDoubleAuthentification {
  enabled: boolean;
  remainingRecoveryCodes: number;
}
